<template>
  <!-- START : Account settings view -->
  <div class="w-100">
    <div class="container-fluid h-100">
      <div
        class="account-info"
        v-for="accountSetting in getAccountSettingsData()"
        :key="accountSetting.name"
        :id="accountSetting.name"
      >
        <!-- START : Setting section header with name and route(edit) -->
        <div class="account-section-header row align-items-center">
          <h3 class="col-10 p-0 mb-0">{{ accountSetting.name }}</h3>
          <div class="col-2 p-0">
            <router-link :to="`/me/edit/${accountSetting.routeName}`" :aria-label="$t('edit')">
              <font-awesome-icon class="account-edit-pencil float-right" icon="pencil"
            /></router-link>
          </div>
        </div>
        <!-- END : Setting section header with name and route(edit) -->

        <!-- START : Setting section details with name and value -->
        <div
          class="account-edit row flex-column px-3 py-2"
          v-for="setting in accountSetting.accountData"
          :key="setting.id ? `${setting.settingName} ${setting.id}` : `${setting.settingName}`"
        >
          <div class="account-subsection">
            {{ setting.settingName }}

            <font-awesome-icon
              v-if="setting.settingIcon"
              class="text-success"
              :icon="setting.settingIcon"
            />
          </div>
          <div class="account-edit-text pt-1" v-if="setting.settingValue">
            {{ setting.settingValue }}
          </div>

          <div v-else-if="setting.option === 'REQUIRED'" class="pt-1">
            <router-link
              :to="`/me/edit/${accountSetting.routeName}`"
              class="account-edit-text link-danger underline-link-from-center"
            >
              {{ $t('pleaseEnterYour', { name: setting.settingName }) }}
            </router-link>
          </div>

          <div v-else-if="setting.option === 'OPTIONAL'" class="account-edit-text pt-1 font-italic">
            {{ $t('notEntered', { name: setting.settingName }) }}
          </div>
        </div>
        <!-- END : Setting section details with name and value -->
      </div>
      <h3 class="account-section-header row">{{ $t('password') }}</h3>
      <router-link to="/me/password" class="underline-link-from-center"
        ><div class="row text-primary px-3 py-2">{{ $t('changePassword') }}</div></router-link
      >
      <!-- Removing two factor authentication -->
      <!-- <div class="row align-items-center">
        <div class="col">
          <p class="account-text my-auto">Two-Factor Authentication</p>
          <p class="account-link my-auto">SET UP TWO-FACTOR AUTHENICATION</p>
        </div>
        <div class="col-3">
          <p class="account-text-small float-right">What's This?</p>
        </div>
      </div> -->
    </div>
  </div>
  <!-- END : Account settings view -->
</template>

<script>
import { ACCOUNT_SETTINGS_ROUTE_CMP_MAP } from '@/constants/AppConstants';
import { format } from 'date-fns';

export default {
  name: 'WCAccountSettingsMobile',
  props: {
    me: {
      type: Object,
    },
  },
  methods: {
    /**
     * Method to find description based on value property to be displayed in UI for the given select field
     * @param {String} select
     * returns description
     */
    findDescriptionFromOptions(select) {
      if (this.me[select].value)
        return this.me[select].options.find(option => option.id === this.me[select].value)
          .description;
      return null;
    },
    /**
     * Method to construct personal information in json format based on account data(me)
     */
    getPersonalInfo() {
      const personalInfo = [];
      if (this.me.prefix) {
        personalInfo.push({
          settingName: this.$t(this.me.prefix.name),
          settingValue: this.findDescriptionFromOptions('prefix'),
          option: this.me.prefix.option,
        });
      }
      if (this.me.firstName) {
        personalInfo.push({
          settingName: this.$t(this.me.firstName.name),
          settingValue: this.me.firstName.value,
          option: this.me.firstName.option,
        });
      }
      if (this.me.middleName) {
        personalInfo.push({
          settingName: this.$t(this.me.middleName.name),
          settingValue: this.me.middleName.value,
          option: this.me.middleName.option,
        });
      }
      if (this.me.lastName) {
        personalInfo.push({
          settingName: this.$t(this.me.lastName.name),
          settingValue: this.me.lastName.value,
          option: this.me.lastName.option,
        });
      }
      if (this.me.suffix) {
        personalInfo.push({
          settingName: this.$t(this.me.suffix.name),
          settingValue: this.findDescriptionFromOptions('suffix'),
          option: this.me.suffix.option,
        });
      }
      if (this.me.nickName) {
        personalInfo.push({
          settingName: this.$t('nickname'),
          settingValue: this.me.nickName.value,
          option: this.me.nickName.option,
        });
      }
      if (this.me.birthDate) {
        personalInfo.push({
          settingName: this.$t('birthday'),
          settingValue: this.me.birthDate.value
            ? format(new Date(this.me.birthDate.value), 'MM/dd/yyyy')
            : '',
          option: this.me.birthDate.option,
        });
      }
      return personalInfo;
    },
    /**
     * Method to construct work information in json format based on account data(me)
     */
    getWorkInfo() {
      const workInfo = [];
      if (this.me.company) {
        workInfo.push({
          settingName: this.$t(this.me.company.name),
          settingValue: this.me.company.value,
          option: this.me.company.option,
        });
      }
      if (this.me.title) {
        workInfo.push({
          settingName: this.$t(this.me.title.name),
          settingValue: this.me.title.value,
          option: this.me.title.option,
        });
      }
      return workInfo;
    },
    /**
     * Method to construct other information in json format based on account data(me)
     */
    getOtherInfo() {
      const otherInfo = [];
      if (this.me.pf1) {
        otherInfo.push({
          settingName: this.me.pf1.name,
          settingValue: this.findDescriptionFromOptions('pf1'),
          option: this.me.pf1.option,
        });
      }
      if (this.me.pf2) {
        otherInfo.push({
          settingName: this.me.pf2.name,
          settingValue: this.findDescriptionFromOptions('pf2'),
          option: this.me.pf2.option,
        });
      }
      if (this.me.pf3) {
        otherInfo.push({
          settingName: this.me.pf3.name,
          settingValue: this.findDescriptionFromOptions('pf3'),
          option: this.me.pf3.option,
        });
      }
      if (this.me.pf4) {
        otherInfo.push({
          settingName: this.me.pf4.name,
          settingValue: this.findDescriptionFromOptions('pf4'),
          option: this.me.pf4.option,
        });
      }
      if (this.me.pf5) {
        otherInfo.push({
          settingName: this.me.pf5.name,
          settingValue: this.me.pf5.value,
          option: this.me.pf5.option,
        });
      }
      if (this.me.pf6) {
        otherInfo.push({
          settingName: this.me.pf6.name,
          settingValue: this.me.pf6.value,
          option: this.me.pf6.option,
        });
      }
      if (this.me.pf7) {
        otherInfo.push({
          settingName: this.me.pf7.name,
          settingValue: this.me.pf7.value,
          option: this.me.pf7.option,
        });
      }
      if (this.me.pf8) {
        otherInfo.push({
          settingName: this.me.pf8.name,
          settingValue: this.me.pf8.value,
          option: this.me.pf8.option,
        });
      }
      if (this.me.suppressReceipt)
        otherInfo.push({
          settingName: `Paperless receipt is turned ${
            this.me.suppressReceipt.value ? 'on' : 'off'
          }`,
          settingIcon: 'leaf',
        });
      return otherInfo;
    },
    /**
     * Method to construct email information in json format based on account data(me)
     */
    getEmailInfo() {
      const emailInfo = [];
      if (this.me.emails)
        this.me.emails.forEach(email => {
          emailInfo.push({
            settingName: email.name,
            settingValue: email.email?.value,
            option: email.email?.option,
          });
        });
      return emailInfo;
    },
    /**
     * Method to construct phone information in json format based on account data(me)
     */
    getPhoneInfo() {
      const phoneInfo = [];
      if (this.me.phones)
        this.me.phones.forEach(phone => {
          phoneInfo.push({
            settingName: phone.name,
            settingValue: phone.phone?.value,
            option: phone.phone?.option,
          });
        });
      return phoneInfo;
    },
    /**
     * Method to construct address information in json format based on account data(me)
     */
    getAddressInfo() {
      const addressInfo = [];
      if (this.me.addresses)
        this.me.addresses.forEach(addr => {
          addressInfo.push({
            settingName: addr.name.toUpperCase(),
          });
          if (addr.street1)
            addressInfo.push({
              id: addr.did,
              settingName: this.$t('streetAddress1'),
              settingValue: addr.street1.value,
              option: addr.street1.option,
            });
          if (addr.street2)
            addressInfo.push({
              id: addr.did,
              settingName: this.$t('streetAddress2'),
              settingValue: addr.street2.value,
              option: addr.street2.option,
            });
          if (addr.city)
            addressInfo.push({
              id: addr.did,
              settingName: this.$t('city'),
              settingValue: addr.city.value,
              option: addr.city.option,
            });
          if (addr.state)
            addressInfo.push({
              id: addr.did,
              settingName: this.$t('state'),
              settingValue: addr.state.value,
              option: addr.state.option,
            });
          if (addr.postal)
            addressInfo.push({
              id: addr.did,
              settingName: this.$t('zipCode'),
              settingValue: addr.postal.value,
              option: addr.postal.option,
            });
          if (addr.country)
            addressInfo.push({
              id: addr.did,
              settingName: this.$t('country'),
              settingValue: addr.country.value,
              option: addr.country.option,
            });
        });
      return addressInfo;
    },
    /**
     * Method to construct entire account setting data in json format based on account data(me)
     */
    getAccountSettingsData() {
      const accountSettings = [];
      const personalInfoSettings = this.getPersonalInfo();
      const workInfoSettings = this.getWorkInfo();
      const otherInfoSettings = this.getOtherInfo();
      const emailInfoSettings = this.getEmailInfo();
      const phoneInfoSettings = this.getPhoneInfo();
      const addressInfoSettings = this.getAddressInfo();
      if (personalInfoSettings.length > 0)
        accountSettings.push({
          name: ACCOUNT_SETTINGS_ROUTE_CMP_MAP.profile.name,
          accountData: personalInfoSettings,
          routeName: ACCOUNT_SETTINGS_ROUTE_CMP_MAP.profile.route,
        });
      if (workInfoSettings.length > 0)
        accountSettings.push({
          name: ACCOUNT_SETTINGS_ROUTE_CMP_MAP.work.name,
          accountData: workInfoSettings,
          routeName: ACCOUNT_SETTINGS_ROUTE_CMP_MAP.work.route,
        });
      if (otherInfoSettings.length > 0)
        accountSettings.push({
          name: ACCOUNT_SETTINGS_ROUTE_CMP_MAP.other.name,
          accountData: otherInfoSettings,
          routeName: ACCOUNT_SETTINGS_ROUTE_CMP_MAP.other.route,
        });
      if (emailInfoSettings.length > 0)
        accountSettings.push({
          name: ACCOUNT_SETTINGS_ROUTE_CMP_MAP.email.name,
          accountData: emailInfoSettings,
          routeName: ACCOUNT_SETTINGS_ROUTE_CMP_MAP.email.route,
        });
      if (phoneInfoSettings.length > 0)
        accountSettings.push({
          name: ACCOUNT_SETTINGS_ROUTE_CMP_MAP.phone.name,
          accountData: phoneInfoSettings,
          routeName: ACCOUNT_SETTINGS_ROUTE_CMP_MAP.phone.route,
        });
      if (addressInfoSettings.length > 0)
        accountSettings.push({
          name: ACCOUNT_SETTINGS_ROUTE_CMP_MAP.address.name,
          accountData: addressInfoSettings,
          routeName: ACCOUNT_SETTINGS_ROUTE_CMP_MAP.address.route,
        });
      return accountSettings;
    },
  },
};
</script>

<style scoped lang="scss">
@import '~@/assets/styles/tools';
@import '~@/assets/styles/settings';

.account-header {
  width: 100%;
  height: 4rem;
  background-color: var(--primary, $primary);
}

.account-section-header {
  background: var(--secondary-lighter-6, get-color('secondary', 'lighter-6'));
  color: var(--dark, $dark);
  padding: $pad-2 $pad-3;
}

.account-subsection {
  font-size: $font-size-base;
  font-weight: 600;
  color: var(--dark);
}
.account-info {
  .account-edit {
    &:last-child {
      border: 0;
    }
  }
}

.account-edit {
  border-bottom: 7px solid var(--light-darker-1);
}
.account-edit-text {
  color: var(--dark);
  font-size: $font-size-base;
}
.account-edit-pencil {
  color: var(--primary, $primary);
  font-size: $font-size-xl;
}
</style>
